// 📄 pages/Dashboard/ProjectSidebar.tsx
// Menú lateral del proyecto, de 2 niveles: barra de íconos (nivel 1,
// siempre visible, ver menuConfig.tsx) + panel secundario (nivel 2, con
// los items de la sección elegida). Reemplaza al viejo <aside> de una
// sola columna de HomeCA.tsx — la navegación en sí (onNavigate = el `go()`
// de HomeCA.tsx) y las rutas no cambian, esto es solo la UI que las
// dispara.
import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import styles from "./ProjectSidebar.module.css";
import {
  SECCIONES_META, MenuItemDef, funcionalidadesItems, HERRAMIENTAS_ITEMS,
  PROYECTO_ITEMS, CONFIGURACION_ITEMS, CANALES_COMMERCE_ITEMS, seccionDeRuta, IconoDe
} from "./menuConfig.tsx";
import { SOLUCIONES, OTRAS_SOLUCIONES } from "../Proyectos/solucionesConfig.ts";
import { FLOW_EXTENSION_API_HABILITADO } from "../Ariba/CommerceHome.tsx";
import { Project, getMemberPermisos } from "../../services/projectService.ts";
import { useAuth } from "../../context/AuthContext.tsx";
import { esErpCloud } from "../views/AribaCommerce/Integracion/useGeneralConfig.ts";
import { esProyectoSap, etiquetaDocumentacion } from "../Proyectos/modulosConfig.ts";

interface ProjectSidebarProps {
  project: Project;
  activeSection: string;
  esCliente: boolean;
  onNavigate: (path: string) => void;
}

// Fallback de persistencia: solo importa cuando la ruta actual no
// pertenece a ninguna sección (ej. la landing, ver seccionDeRuta) — para
// cualquier otra ruta, la sección dueña ya se deriva de location.pathname,
// sin depender de esto.
const seccionKey = (projectId: string) => `onehub_sidebar_seccion_${projectId}`;

export default function ProjectSidebar({ project, activeSection, esCliente, onNavigate }: ProjectSidebarProps) {
  const { user } = useAuth();
  // Permisos del propio usuario en ESTE proyecto — ver MemberPermisos en
  // projectService.ts (projects/{id}/permisos/{uid}, fuente de verdad
  // real, ya no un campo de project.miembros). Mientras carga (o si nunca
  // se configuró nada, o el usuario ve el proyecto sin ser miembro real —
  // guest/owner de org) se trata como "nada oculto", mismo criterio de
  // siempre. Solo Incidentes/Miembros/Editar Proyecto tienen protección
  // REAL en firestore.rules — el resto de lo que esto oculta es solo UI.
  const [misSeccionesOcultas, setMisSeccionesOcultas] = useState<string[]>([]);
  const [misSolucionesOcultas, setMisSolucionesOcultas] = useState<string[]>([]);

  useEffect(() => {
    if (!user) { setMisSeccionesOcultas([]); setMisSolucionesOcultas([]); return; }
    let cancelado = false;
    getMemberPermisos(project.id, user.uid)
      .then((permisos) => {
        if (cancelado) return;
        setMisSeccionesOcultas(permisos?.seccionesOcultas || []);
        setMisSolucionesOcultas(permisos?.solucionesOcultas || []);
      })
      .catch((err) => console.error('Error cargando mis permisos de sidebar:', err));
    return () => { cancelado = true; };
  }, [project.id, user]);

  // Sin SAP Ariba, lo que vive en "Herramientas" (Credenciales) se muestra en "Proyecto".
  const seccionDe = (ruta: string) => {
    const s = seccionDeRuta(ruta);
    return !esProyectoSap(project) && s === "herramientas" ? "proyecto" : s;
  };

  const [seccionAbierta, setSeccionAbierta] = useState<string | null>(() => {
    const deRuta = seccionDe(activeSection);
    if (deRuta) return deRuta;
    try {
      return localStorage.getItem(seccionKey(project.id)) || null;
    } catch {
      return null;
    }
  });

  // Si la ruta activa cambia por una navegación que no vino de este
  // sidebar (un <Link>, "Volver a Inicio", history back, etc.) y la nueva
  // ruta pertenece a otra sección, el panel se sincroniza solo. Si no
  // pertenece a ninguna (ej. la landing), se deja lo que el usuario haya
  // dejado abierto manualmente.
  useEffect(() => {
    const deRuta = seccionDe(activeSection);
    if (deRuta) setSeccionAbierta(deRuta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  useEffect(() => {
    try {
      if (seccionAbierta) localStorage.setItem(seccionKey(project.id), seccionAbierta);
    } catch {
      // localStorage puede fallar (modo privado, cuota) — solo se pierde
      // el fallback de persistencia, no la navegación en sí.
    }
  }, [seccionAbierta, project.id]);

  const toggleSeccion = (id: string) => {
    setSeccionAbierta((prev) => (prev === id ? null : id));
  };

  // Proyectos sin el módulo SAP Ariba: sin Soluciones ni ítems SAP (ver menuConfig.ts, soloSap).
  const sap = esProyectoSap(project);
  const solucionesActivas = project.solucionesActivas || ["commerce"];
  const isCommerceActive = solucionesActivas.includes("commerce");
  // Mismo criterio que tenía HomeCA.tsx: los Canales de Integración de
  // Commerce viven en "integracion/{canal}" (un solo segmento) — distinto
  // de "integracion/{solucion}/{canal}" (dos segmentos) de las demás
  // soluciones, que no tienen link propio en este sidebar.
  // "integracion/generales" queda excluido a propósito: es transversal a
  // CUALQUIER solución (ver IntegrationSection.tsx), no un canal propio de
  // Commerce — si no se excluyera, el sidebar mostraría los canales de
  // Commerce (OC, ASN, ...) incluso en un proyecto sin Commerce activo.
  const isIntegrationSection = /^integracion\/[^/]+$/.test(activeSection) && activeSection !== "integracion/generales";

  const itemsPorSeccion = (id: string): MenuItemDef[] => {
    switch (id) {
      case "soluciones":
        if (!sap) return [];
        if (isIntegrationSection) return CANALES_COMMERCE_ITEMS.filter((item) => !item.onPremOnly || !esErpCloud(project.erpTipo));
        return [
          ...(isCommerceActive ? [{ id: "commerce", label: "Commerce Automation", icon: SOLUCIONES.commerce.icon }] : []),
          ...OTRAS_SOLUCIONES.filter((s) => solucionesActivas.includes(s.slug)).map((s) => ({ id: s.path, label: s.label, icon: s.icon }))
        ].filter((item) => !misSolucionesOcultas.includes(item.id));
      case "funcionalidades":
        return funcionalidadesItems(FLOW_EXTENSION_API_HABILITADO, solucionesActivas.includes("slp"), isCommerceActive)
          .map((item) => (item.id === "especificaciones" ? { ...item, label: etiquetaDocumentacion(project) } : item));
      // Sin SAP Ariba la única herramienta es Credenciales: pasa a "Proyecto"
      // y el ícono de Herramientas desaparece (plan-proyectos-generales, G3).
      case "herramientas":
        return sap ? HERRAMIENTAS_ITEMS : [];
      case "proyecto":
        return sap ? PROYECTO_ITEMS : [...PROYECTO_ITEMS, ...HERRAMIENTAS_ITEMS.filter((i) => !i.soloSap)];
      case "configuracion":
        return CONFIGURACION_ITEMS;
      default:
        return [];
    }
  };

  const itemsVisibles = (id: string): MenuItemDef[] =>
    itemsPorSeccion(id).filter(
      (item) =>
        (!item.soloConsultor || !esCliente) &&
        (!item.soloSap || sap) &&
        // Apagarla solo impide crear tickets: los que ya existen se siguen atendiendo.
        (!item.soloMesaAyuda || !!project.recibeTickets || (project.ticketsContador || 0) > 0) &&
        !misSeccionesOcultas.includes(item.id)
    );

  // En el landing del proyecto (activeSection === "") seccionDeRuta()
  // devuelve null a propósito (no debe auto-abrir el panel de Soluciones
  // encima de la página de inicio, que ya muestra esas mismas soluciones
  // como cards) — pero el puntito de "activo" sí debe marcar "Soluciones"
  // ahí, así que se resuelve aparte en vez de tocar seccionDeRuta.
  const seccionActivaId = seccionDe(activeSection) ?? (activeSection === "" ? (sap ? "soluciones" : "proyecto") : null);
  const seccionAbiertaMeta = seccionAbierta ? SECCIONES_META.find((s) => s.id === seccionAbierta) : null;
  const itemsDelPanel = seccionAbierta ? itemsVisibles(seccionAbierta) : [];

  return (
    <div className={styles.root}>
      <nav className={styles.iconBar}>
        {SECCIONES_META.map((sec) => {
          const visibles = itemsVisibles(sec.id);
          // Sección sin items visibles para este rol (hoy solo pasa con
          // "Configuración" para un cliente, ver menuConfig.tsx) -> ni el
          // ícono se muestra.
          if (visibles.length === 0) return null;

          const activa = seccionActivaId === sec.id;
          const abierta = seccionAbierta === sec.id;
          const Icon = sec.icon;

          return (
            <button
              key={sec.id}
              type="button"
              title={sec.label}
              onClick={() => toggleSeccion(sec.id)}
              className={`${styles.iconButton} ${abierta ? styles.iconButtonAbierto : ""}`}
            >
              <Icon size={20} />
              {activa && <span className={styles.indicadorActivo} />}
              <span className={styles.iconLabel}>{sec.labelCorta}</span>
            </button>
          );
        })}

        <div className={styles.iconBarSpacer} />

        <button
          type="button"
          title="Volver a Inicio"
          onClick={() => onNavigate("")}
          className={styles.iconButton}
        >
          <ChevronRight size={20} className="rotate-180" />
          <span className={styles.iconLabel}>Inicio</span>
        </button>
      </nav>

      <div className={`${styles.panel} ${seccionAbierta ? styles.panelAbierto : ""}`}>
        {seccionAbiertaMeta && (
          <div className={styles.panelContenido}>
            {esCliente && (
              <span className={styles.badgeCliente}>Modo Cliente · Solo lectura</span>
            )}
            <div className={styles.panelTitulo}>{seccionAbiertaMeta.label}</div>
            {itemsDelPanel.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onNavigate(item.id)}
                className={`${styles.navButton} ${activeSection === item.id ? styles.activeButton : ""}`}
              >
                <IconoDe icon={item.icon} size={18} />
                <span className="text-sm font-medium truncate">{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {seccionAbierta && (
        <div className={styles.backdrop} onClick={() => setSeccionAbierta(null)} />
      )}
    </div>
  );
}
